// Shared display constants for member skill profiles (derived + self-declared).

export const TAG_TYPE_LABELS = {
  tech: 'Technologies',
  language: 'Languages',
  framework: 'Frameworks',
  tool: 'Tools',
  domain: 'Domains',
  industry: 'Industries',
  feature_type: 'Feature types',
};

// Order tag-type groups appear in the UI.
export const TAG_TYPE_ORDER = [
  'language',
  'framework',
  'tech',
  'tool',
  'domain',
  'industry',
  'feature_type',
];

export const PROFICIENCY_LABELS = {
  expert: 'Expert',
  proficient: 'Proficient',
  working: 'Working',
  exposure: 'Exposure',
  learning: 'Learning',
};

// Tailwind classes per proficiency band (dark theme).
export const PROFICIENCY_BADGE_CLASS = {
  expert: 'bg-vivid-green-cyan/15 text-vivid-green-cyan',
  proficient: 'bg-blue-500/15 text-blue-300',
  working: 'bg-amber-500/15 text-amber-300',
  exposure: 'bg-slate-500/20 text-slate-300',
  learning: 'bg-slate-500/20 text-slate-300',
};

export const DECLARATION_LEVELS = ['expert', 'proficient', 'working', 'exposure', 'learning'];

// Tag types a member may self-declare (matches backend VALID_TAG_TYPES).
export const DECLARABLE_TAG_TYPES = [
  'tech',
  'language',
  'framework',
  'tool',
  'domain',
  'industry',
  'feature_type',
];

export function proficiencyBadgeClass(signal) {
  return (
    PROFICIENCY_BADGE_CLASS[String(signal || '').toLowerCase()] || 'bg-slate-500/20 text-slate-300'
  );
}

export function proficiencyLabel(signal) {
  const key = String(signal || '').toLowerCase();
  return PROFICIENCY_LABELS[key] || (key ? key : 'N/A');
}

export function tagTypeLabel(tagType) {
  return TAG_TYPE_LABELS[tagType] || tagType;
}

export const SKILL_ICON_ALIASES: Record<string, string> = {
  nodejs: 'nodedotjs',
  node: 'nodedotjs',
  nextjs: 'nextdotjs',
  next: 'nextdotjs',
  nuxtjs: 'nuxtdotjs',
  nuxt: 'nuxtdotjs',
  vuejs: 'vuedotjs',
  vue: 'vuedotjs',
  go: 'go',
  golang: 'go',
  postgres: 'postgresql',
  postgresql: 'postgresql',
  'github actions': 'githubactions',
  githubactions: 'githubactions',
  'gitlab ci': 'gitlab',
  tailwind: 'tailwindcss',
  tailwindcss: 'tailwindcss',
  vscode: 'visualstudiocode',
  k8s: 'kubernetes',
  kubernetes: 'kubernetes',
  'objective-c': 'c',
  'c#': 'csharp',
  csharp: 'csharp',
  'c++': 'cplusplus',
  express: 'express',
  dotnet: 'dotnet',
  '.net': 'dotnet',
  typescript: 'typescript',
  javascript: 'javascript',
  python: 'python',
  rust: 'rust',
  java: 'openjdk',
  kotlin: 'kotlin',
  swift: 'swift',
  ruby: 'ruby',
  php: 'php',
  react: 'react',
  angular: 'angular',
  svelte: 'svelte',
  docker: 'docker',
  graphql: 'graphql',
  mongodb: 'mongodb',
  redis: 'redis',
  mysql: 'mysql',
  sqlite: 'sqlite',
  prisma: 'prisma',
  nestjs: 'nestjs',
  openai: 'openai',
  github: 'github',
  gitlab: 'gitlab',
  algolia: 'algolia',
  azure: 'microsoftazure',
  aws: 'amazonaws',
  apollo: 'apollographql',
  apollographql: 'apollographql',
  vercel: 'vercel',
  supabase: 'supabase',
  stripe: 'stripe',
  sentry: 'sentry',
  jest: 'jest',
  vitest: 'vitest',
  playwright: 'playwright',
  cypress: 'cypress',
  webpack: 'webpack',
  vite: 'vite',
  eslint: 'eslint',
  prettier: 'prettier',
};

/** npm scope → Simple Icons slug when the package segment is generic (client, config, …). */
const SCOPE_ICON_SLUGS: Record<string, string> = {
  actions: 'github',
  apollo: 'apollographql',
  algolia: 'algolia',
  azure: 'microsoftazure',
  aws: 'amazonaws',
  'aws-sdk': 'amazonaws',
  prisma: 'prisma',
  nestjs: 'nestjs',
  angular: 'angular',
  tanstack: 'reactquery',
  tailwindcss: 'tailwindcss',
  vercel: 'vercel',
  supabase: 'supabase',
  sentry: 'sentry',
  stripe: 'stripe',
  'ai-sdk': 'openai',
  'as-integrations': 'express',
};

const GENERIC_PACKAGE_SEGMENTS =
  /^(client|server|core|types|config|identity|integration|integrations|react-hooks|validator|search)(-|$)/i;

const TRUSTED_ICON_SLUGS = new Set([
  ...Object.values(SKILL_ICON_ALIASES),
  ...Object.values(SCOPE_ICON_SLUGS),
  'amazonaws',
  'microsoftazure',
  'reactrouter',
  'reactquery',
  'radixui',
  'shadcnui',
]);

export const ICONABLE_TAG_TYPES = new Set(['tech', 'language', 'framework', 'tool']);

/** Pinned simple-icons release — jsdelivr serves the full set; cdn.simpleicons.org omits many brands. */
const SIMPLE_ICONS_CDN_VERSION = '11.14.0';

/** Brand SVGs are often black/dark; render as light monochrome on our dark UI. */
export const SKILL_ICON_IMG_CLASS = 'brightness-0 invert opacity-80';

function buildSimpleIconsSlug(name: string) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/\+/g, 'plus')
    .replace(/#/g, 'sharp')
    .replace(/\./g, 'dot')
    .replace(/\s+/g, '')
    .replace(/[^a-z0-9]/g, '');
}

function parseScopedPackageName(name: string): { scope: string; pkg: string } | null {
  const trimmed = String(name || '').trim();
  const atMatch = /^@([^/]+)\/(.+)$/.exec(trimmed);
  if (atMatch) return { scope: atMatch[1], pkg: atMatch[2] };
  const slashMatch = /^([a-z0-9][a-z0-9-]*)\/([^/\s]+)$/i.exec(trimmed);
  if (slashMatch) return { scope: slashMatch[1], pkg: slashMatch[2] };
  return null;
}

function firstPackageToken(pkg: string) {
  return String(pkg || '')
    .toLowerCase()
    .split('/')[0]
    .split('-')
    .filter(Boolean)[0];
}

function resolveSlugFromScopedPackage(scope: string, pkg: string): string | null {
  const scopeL = scope.toLowerCase();
  const pkgL = pkg.toLowerCase();
  const pkgBase = firstPackageToken(pkg);

  if (SKILL_ICON_ALIASES[pkgL]) return SKILL_ICON_ALIASES[pkgL];
  if (SKILL_ICON_ALIASES[pkgBase]) return SKILL_ICON_ALIASES[pkgBase];

  if (pkgL.includes('openai')) return 'openai';
  if (pkgL.includes('github')) return 'github';
  if (pkgL.includes('express')) return 'express';
  if (pkgL.includes('lambda') && (scopeL.includes('aws') || scopeL === 'as-integrations')) {
    return 'amazonaws';
  }

  if (SCOPE_ICON_SLUGS[scopeL]) {
    if (GENERIC_PACKAGE_SEGMENTS.test(pkgL) || pkgL.length > 22) {
      return SCOPE_ICON_SLUGS[scopeL];
    }
  }

  const scopeSlug = buildSimpleIconsSlug(scope);
  if (TRUSTED_ICON_SLUGS.has(scopeSlug)) return scopeSlug;

  return null;
}

/** Resolve a Simple Icons CDN slug, or null when the skill name is not a known brand. */
export function resolveSimpleIconSlug(name: string): string | null {
  const trimmed = String(name || '').trim();
  if (!trimmed) return null;

  const lower = trimmed.toLowerCase();
  if (SKILL_ICON_ALIASES[lower]) return SKILL_ICON_ALIASES[lower];

  const scoped = parseScopedPackageName(trimmed);
  if (scoped) {
    return resolveSlugFromScopedPackage(scoped.scope, scoped.pkg);
  }

  // Already-normalized display labels (e.g. "Tailwind CSS") — not npm slugs.
  if (/\s/.test(trimmed)) {
    const slug = buildSimpleIconsSlug(trimmed);
    return slug && TRUSTED_ICON_SLUGS.has(slug) ? slug : null;
  }

  const slug = buildSimpleIconsSlug(trimmed);
  if (!slug || slug.length > 18) return null;
  if (!TRUSTED_ICON_SLUGS.has(slug)) return null;
  return slug;
}

export function simpleIconsSvgUrl(slug: string): string {
  return `https://cdn.jsdelivr.net/npm/simple-icons@${SIMPLE_ICONS_CDN_VERSION}/icons/${encodeURIComponent(slug)}.svg`;
}

export type SkillIconMeta = { url: string };

/** Icon URL for a skill tag, or null when no trusted brand match. */
export function getSkillIconMeta(name: string, tagType: string): SkillIconMeta | null {
  if (!ICONABLE_TAG_TYPES.has(tagType)) return null;
  const slug = resolveSimpleIconSlug(name);
  if (!slug) return null;
  return { url: simpleIconsSvgUrl(slug) };
}

/**
 * Simple Icons SVG URL for a skill tag, or null when not applicable / empty name.
 */
export function skillIconUrl(name: string, tagType: string) {
  return getSkillIconMeta(name, tagType)?.url ?? null;
}

/** Group an array of tags by tagType, preserving sort order within each group. */
export function groupTagsByType(tags) {
  const groups = new Map();
  for (const tag of tags || []) {
    if (tag.userReview === 'rejected') continue;
    if (!groups.has(tag.tagType)) groups.set(tag.tagType, []);
    groups.get(tag.tagType).push(tag);
  }
  return TAG_TYPE_ORDER.filter((t) => groups.has(t))
    .map((t) => ({ tagType: t, tags: groups.get(t) }))
    .concat(
      [...groups.keys()]
        .filter((t) => !TAG_TYPE_ORDER.includes(t))
        .map((t) => ({ tagType: t, tags: groups.get(t) }))
    );
}
